import { ElementRef } from '@angular/core';
import { v4 as uuid } from 'uuid';
import { COptionsResponse } from '../clases/COptionsResponse';
import { Shape } from '../enums/Shape';
import { RelationDirection } from '../enums/RelationDirection';
import { TreeMenuAction } from '../enums/TreeMenuAction';
import { ISchemaTree } from '../interfaces/SchemaTree';
import { SchemaTree } from '../clases/SchemaTree';
import { DBItem } from '../interfaces/Record';
import { ImportEnum } from '../enums/Import';
import { ArrowStyle, ArrowType } from '../enums/ArrowType';
import { Anchor } from '../enums/Anchor';
import { TimeUnit } from '../enums/TimeUnit';

export function generateUUID(){
    let id = uuid();
    if (!isNaN(parseInt(id[0], 16))) {
        let letters = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'x', 'y', 'z'];
        const randomIndex = Math.floor(Math.random() * letters.length);
        id = letters[randomIndex] + id.slice(1);
    }
    return id;
}

export function extractTranslateValues(value: string){
    let match = /translate3d\((?<x>.*?)px, (?<y>.*?)px, (?<z>.*?)px/;
    return match.exec(value);
}


export function extractFromCypher(value: string, nodes: boolean = true){
    let cypherExtractReg = /\((\w+)(?::(\w+(?::\w+)*))?(?:\s*\{[^}]*\})?(?:\s+where\s+[^)]+)?\)|\[(\w+)(?::(\w+))?(?:\s*\{[^}]*\})?(?:\s+where\s+[^\]]+)?\]/g;
    let match;
    const allMatches = [];
    while ((match = cypherExtractReg.exec(value)) !== null) { allMatches.push(match); }
    let filtered = allMatches.map((element)=>{
        if(element) return nodes ? element['1'] : element['3'];
        return undefined;
    })
    filtered = filtered.filter(element => element !== undefined);
    return filtered || [];
}

export function extractLabelFromCypher(value: string, nodes: boolean = true){
    let cypherExtractReg = /\((\w+)(?::(\w+(?::\w+)*))?(?:\s*\{[^}]*\})?(?:\s+where\s+[^)]+)?\)|\[(\w+)(?::(\w+))?(?:\s*\{[^}]*\})?(?:\s+where\s+[^\]]+)?\]/g;
    let match;
    const allMatches = [];
    while ((match = cypherExtractReg.exec(value)) !== null) { allMatches.push(match); }
    let filtered = allMatches.map((element)=>{
        if(element) return { name: element['1'], value: element['2']};
        return undefined;
    })
    filtered = filtered.filter(element => element !== undefined);
    return filtered || [];
}

export function extractRelationFromCypher(value: string, nodes: boolean = true){
    let cypherExtractReg = /\((\w+)(?::(\w+(?::\w+)*))?(?:\s*\{[^}]*\})?(?:\s+where\s+[^)]+)?\)|\[(\w+)(?::(\w+))?(?:\s*\{[^}]*\})?(?:\s+where\s+[^\]]+)?\]/g;
    let match;
    const allMatches = [];
    while ((match = cypherExtractReg.exec(value)) !== null) { allMatches.push(match); }
    let filtered = allMatches.map((element)=>{
        if(element) return { name: element['3'], value: element['4']};
        return undefined;
    })
    filtered = filtered.filter(element => element !== undefined);
    return filtered || [];
}


export function extractTransform(element: ElementRef){
    if(!element)return null;
    return element.nativeElement.style.transform;
}

export function extractElementBounding(element: ElementRef){
    if(!element)return null;
    return element.nativeElement.getBoundingClientRect();
}

export function getHierarchyLabels(hierarchy: COptionsResponse[], list ?: string[]): string[]{
    if (!list) list = [];
    for (const h of hierarchy) {
        list = [...list, h.label];
        if (h.containers.length > 0) {
            list = [...list, ...getHierarchyLabels(h.containers as COptionsResponse[], list)];
        }
    }
    list = list.filter((value, index) => list!.indexOf(value) === index);
    return list;
}

export function convertIntoShapeEnum(shape: string){
    const enumKey = Object.keys(Shape).find(
        key => Shape[key as keyof typeof Shape] === shape
      );
      const enumValue = enumKey ? Shape[enumKey as keyof typeof Shape] : Shape.RECTANGLE;
      return enumValue;
}

export function convertIntoArrowEnum(shape: string){
    const enumKey = Object.keys(ArrowStyle).find(
        key => ArrowStyle[key as keyof typeof ArrowStyle] === shape
      );
      const enumValue = enumKey ? ArrowStyle[enumKey as keyof typeof ArrowStyle] : ArrowStyle.SOLID;
      return enumValue;
}

export function convertIntoAnchorEnum(shape: string){
    const enumKey = Object.keys(Anchor).find(
        key => Anchor[key as keyof typeof Anchor] === shape
      );
      const enumValue = enumKey ? Anchor[enumKey as keyof typeof Anchor] : Anchor.ANY;
      return enumValue;
}

export function convertIntoArrowTypeEnum(shape: string){
    const enumKey = Object.keys(ArrowType).find(
        key => ArrowType[key as keyof typeof ArrowType] === shape
      );
      const enumValue = enumKey ? ArrowType[enumKey as keyof typeof ArrowType] : ArrowType.SQUARED;
      return enumValue;
}

export function convertIntoRelationDirectionEnum(shape: string){
    const enumKey = Object.keys(RelationDirection).find(
        key => RelationDirection[key as keyof typeof RelationDirection] === shape
      );
      const enumValue = enumKey ? RelationDirection[enumKey as keyof typeof RelationDirection] : RelationDirection.CHILD_TO_FATHER;
      return enumValue;
}

export function convertIntoTreeMenuActionEnum(shape: string){
    const enumKey = Object.keys(TreeMenuAction).find(
        key => TreeMenuAction[key as keyof typeof TreeMenuAction] === shape
      );
      const enumValue = enumKey ? TreeMenuAction[enumKey as keyof typeof TreeMenuAction] : TreeMenuAction.SHOW_NODE;
      return enumValue;
}

export function convertIntoImporterType(shape: string){
    const enumKey = Object.keys(ImportEnum).find(
        key => ImportEnum[key as keyof typeof ImportEnum] === shape
      );
      const enumValue = enumKey ? ImportEnum[enumKey as keyof typeof ImportEnum] : ImportEnum.IMPORT_NEW_TAB;
      return enumValue;
}

export function createSchemaTreeRecursive(schema: ISchemaTree): SchemaTree {
    let tree = new SchemaTree(schema.label, schema.title, schema.hierarchy, schema.has_children, schema.elementId, schema.UUID);
    if (schema.children) {
        for (const child of schema.children) {
            let returned = createSchemaTreeRecursive(child);
            tree.children = [...tree.children, returned];
        }
    }
    return tree
}

export function obtainFullRelation(startNodeElementId: string, endNodeElementId: string, relationship: string, relations: DBItem[]) {
    return relations.find((rel) =>
        (rel.endNodeElementId === endNodeElementId) &&
        (rel.startNodeElementId === startNodeElementId) &&
        relationship === rel.properties.name);
}

export function obtainRelation(startNodeElementId: string, relationship: string, relations: DBItem[]) {
    return relations.find((rel) =>
        (rel.startNodeElementId === startNodeElementId || rel.endNodeElementId === startNodeElementId) &&
        relationship === rel.properties.name);
}

export function generateCurrentDate(){
    let dateTime = new Date();
    let date = `[${dateTime.getMonth()+1}-${dateTime.getDate()}-${dateTime.getFullYear()}]_[${dateTime.getHours()}-${dateTime.getMinutes()}-${dateTime.getSeconds()}]`;
    return date;
}

const DIVISORS: Record<TimeUnit, number> = {
    [TimeUnit.MINUTES]: 60,
    [TimeUnit.HOUR]: 3600,
    [TimeUnit.DAY]: 86400,
    [TimeUnit.WEEKS]: 604800,
};

export const TimeSuffix: Record<TimeUnit, string> = {
    [TimeUnit.MINUTES]: "M",
    [TimeUnit.HOUR]: "H",
    [TimeUnit.DAY]: "D",
    [TimeUnit.WEEKS]: "W",
};

export function secondsTo(sec: number, unit: TimeUnit): number {
    return sec / DIVISORS[unit];
}